import {
  EditableChip,
  Flex,
  FlexCenter,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as ClockCountdownIcon } from '@elementary/common-ui/assets/16x16/clock-countdown.svg';
import { ReactComponent as FailedRunIcon } from '@elementary/common-ui/assets/16x16/failed-run.svg';
import { formatTimeDuration } from '@elementary/common-ui/utils/datetime';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import useIncidentMetadata from '../../hooks/useIncidentMetadata';
import { isIncidentReadOnly } from '../../utils/incidentState';
import { SEVERITY_DISPLAY, STATUS_DISPLAY } from '../../constants';
import type { IncidentDetail } from '../../types';

const ChipWithIcon = styled(FlexCenter)`
  gap: 4px;
  padding: 4px 6px;
`;

const InfoChip = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.black};
  white-space: nowrap;
`;

type Props = {
  incident: IncidentDetail;
};

const IncidentMetadata = ({ incident }: Props) => {
  const {
    severityOptions,
    statusOptions,
    assigneeOptions,
    canEdit,
    updateStatus,
    updateAssignee,
    updateSeverity,
    isUpdating,
  } = useIncidentMetadata();

  const isResolved = incident.status === 'RESOLVED';
  const isReadOnly = isIncidentReadOnly(incident);
  const isEditDisabled = !canEdit || isResolved;

  const failedMonitorCount = incident.monitors.filter(
    (m) => m.signal.isActive,
  ).length;

  return (
    <Flex gap={1} alignItems="center" flexWrap="wrap">
      {!isReadOnly && (
        <>
          <EditableChip
            value={incident.assignee || NO_ASSIGNEE_VALUE}
            options={assigneeOptions ?? []}
            disabled={isEditDisabled}
            onSave={(value) =>
              updateAssignee({
                incidentIds: [incident.id],
                assignee: value === NO_ASSIGNEE_VALUE ? null : value,
              })
            }
            showSearch
            isLoading={isUpdating}
          />
          <EditableChip
            value={incident.status}
            options={statusOptions}
            disabled={isEditDisabled}
            onSave={(value) =>
              updateStatus({
                incidentIds: [incident.id],
                status: STATUS_DISPLAY[value.toLowerCase()] ?? value,
              })
            }
            isLoading={isUpdating}
          />
          <EditableChip
            value={incident.severity}
            options={severityOptions}
            disabled={isEditDisabled}
            onSave={(value) =>
              updateSeverity({
                incidentIds: [incident.id],
                severity: SEVERITY_DISPLAY[value.toLowerCase()] ?? value,
              })
            }
            isLoading={isUpdating}
          />
        </>
      )}

      <ChipWithIcon>
        <ClockCountdownIcon />
        <InfoChip>Open {formatTimeDuration(incident.startTime)}</InfoChip>
      </ChipWithIcon>

      <ChipWithIcon>
        <FailedRunIcon />
        <InfoChip>
          {pluralizeString(
            failedMonitorCount,
            'failed monitor',
            'failed monitors',
          )}
        </InfoChip>
      </ChipWithIcon>
    </Flex>
  );
};

export default IncidentMetadata;
